/**
 * `optimisticFlipMutation` — React Query mutation factory for the
 * "optimistic boolean flip on a paginated/infinite list" pattern.
 *
 * Per NFR-Design § 1: disable + enable + edit-profile use the optimistic
 * shape; create / soft-delete / rotate-key / retire-key stay pessimistic.
 *
 * Supports BOTH `useQuery`-shaped caches (`Paginated<T>`) AND
 * `useInfiniteQuery` caches (`{ pages: Paginated<T>[], pageParams: ... }`)
 * so the same helper drives the list page (infinite) and any single-page
 * surface.
 */

import {
  type QueryClient,
  type QueryKey,
  useMutation,
  type UseMutationResult,
} from "@tanstack/react-query";
import { toast } from "@cometchat-ai/ui-dashboard";

import type { Paginated } from "./types";

interface InfiniteCache<T> {
  pages: Paginated<T>[];
  pageParams: unknown[];
}

type ListCache<T> = Paginated<T> | InfiniteCache<T> | undefined;

function isInfinite<T>(c: NonNullable<ListCache<T>>): c is InfiniteCache<T> {
  return Array.isArray((c as InfiniteCache<T>).pages);
}

function patchCache<T extends { id: string }>(
  cache: ListCache<T>,
  id: string,
  patcher: (item: T) => T,
): ListCache<T> {
  if (!cache) return cache;
  if (isInfinite(cache)) {
    return {
      ...cache,
      pages: cache.pages.map((page) => ({
        ...page,
        items: page.items.map((item) => (item.id === id ? patcher(item) : item)),
      })),
    };
  }
  return {
    ...cache,
    items: cache.items.map((item) => (item.id === id ? patcher(item) : item)),
  };
}

export interface OptimisticFlipOptions<T extends { id: string }, V = string> {
  queryClient: QueryClient;
  /** List query key (single or infinite). Patched in-place. */
  listKey: QueryKey;
  /** Detail query key derived from the variables. Patched + invalidated on settle. */
  detailKey?: (id: string) => QueryKey;
  /** Extract the contact id from the mutation variables. Defaults to identity if `V` is a string. */
  toId?: (vars: V) => string;
  mutationFn: (vars: V) => Promise<T>;
  /** Optimistic patch applied to the matching cached item. */
  optimisticPatch: (current: T) => T;
  successToast?: string;
  errorToast: string;
}

interface Context<T> {
  previousList: ListCache<T>;
  previousDetail: T | undefined;
  detailKey: QueryKey | undefined;
}

export function useOptimisticFlipMutation<T extends { id: string }, V = string>(
  options: OptimisticFlipOptions<T, V>,
): UseMutationResult<T, Error, V, Context<T>> {
  const toId = options.toId ?? ((vars: V) => vars as unknown as string);

  return useMutation<T, Error, V, Context<T>>({
    mutationFn: options.mutationFn,
    onMutate: async (vars) => {
      const id = toId(vars);
      const detailKey = options.detailKey?.(id);

      await options.queryClient.cancelQueries({ queryKey: options.listKey });
      if (detailKey) await options.queryClient.cancelQueries({ queryKey: detailKey });

      const previousList = options.queryClient.getQueryData<ListCache<T>>(options.listKey);
      const previousDetail = detailKey ? options.queryClient.getQueryData<T>(detailKey) : undefined;

      options.queryClient.setQueryData<ListCache<T>>(options.listKey, (current) =>
        patchCache(current, id, options.optimisticPatch),
      );
      if (detailKey && previousDetail) {
        options.queryClient.setQueryData<T>(detailKey, options.optimisticPatch(previousDetail));
      }

      return { previousList, previousDetail, detailKey };
    },
    onError: (_err, _vars, context) => {
      if (!context) return;
      options.queryClient.setQueryData(options.listKey, context.previousList);
      if (context.detailKey && context.previousDetail) {
        options.queryClient.setQueryData(context.detailKey, context.previousDetail);
      }
      toast.error(options.errorToast);
    },
    onSuccess: () => {
      if (options.successToast) toast.success(options.successToast);
    },
    onSettled: (_data, _err, vars) => {
      const id = toId(vars);
      void options.queryClient.invalidateQueries({ queryKey: options.listKey });
      const detailKey = options.detailKey?.(id);
      if (detailKey) void options.queryClient.invalidateQueries({ queryKey: detailKey });
    },
  });
}
